import { useState, useEffect } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { useProcurement } from "@/context/ProcurementContext";
import { useAlerts } from "@/context/AlertContext";
import { 
  QualityInspectionList, 
  QualityInspectionForm, 
  QualityInspectionDetails, 
  QualityDashboard 
} from "@/components/quality";
import { IQCTestingModal } from "@/components/quality/IQCTestingModal";
import { InvoiceQualityTestingModal } from "@/components/quality/InvoiceQualityTestingModal";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Plus, 
  FileText, 
  BarChart3, 
  List,
  Search,
  Filter,
  Download,
  MessageCircle,
  Building2
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { formatCurrency } from "@/lib/utils";
import { toast } from "sonner";

const QualityManagement = () => {
  const { state, actions } = useProcurement();
  const { addAlert } = useAlerts();
  const [activeTab, setActiveTab] = useState("iqc-ready");
  const [showForm, setShowForm] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState<number | null>(null);
  const [editingInspection, setEditingInspection] = useState<number | null>(null);
  const [showIQCModal, setShowIQCModal] = useState(false);
  const [showInvoiceQualityModal, setShowInvoiceQualityModal] = useState(false);
  const [selectedPO, setSelectedPO] = useState<any>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);

  useEffect(() => {
    actions.fetchQualityInspections();
    actions.fetchInvoices();
    actions.fetchPurchaseOrders();
  }, []);

  // Force re-render when invoices change
  useEffect(() => {
    console.log('Invoices state updated:', state.invoices.length, 'invoices');
  }, [state.invoices]);

  const handleCreateInspection = () => {
    setEditingInspection(null);
    setShowForm(true);
  };

  const handleEditInspection = (id: number) => {
    setEditingInspection(id);
    setShowForm(true);
  };

  const handleViewInspection = (id: number) => {
    setSelectedInspection(id);
    setShowDetails(true);
  };

  const handleSaveInspection = async (inspectionData: any): Promise<boolean> => {
    try {
      if (editingInspection) {
        // Update existing inspection
        const success = await actions.updateQualityInspection(editingInspection, inspectionData);
        if (success) {
          toast.success("Quality inspection updated successfully");
          setShowForm(false);
          setEditingInspection(null);
          return true;
        }
        return false;
      } else {
        // Create new inspection
        const success = await actions.createQualityInspection(inspectionData);
        if (success) {
          toast.success("Quality inspection created successfully");
          setShowForm(false);
          return true;
        }
        return false;
      }
    } catch (error) {
      console.error("Failed to save inspection:", error);
      toast.error("Failed to save quality inspection");
      return false;
    }
  };

  const handleDeleteInspection = async (id: number) => {
    try {
      const success = await actions.deleteQualityInspection(id);
      if (success) {
        toast.success("Quality inspection deleted successfully");
      }
    } catch (error) {
      console.error("Failed to delete inspection:", error);
      toast.error("Failed to delete quality inspection");
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingInspection(null);
  };

  const handleCloseDetails = () => {
    setShowDetails(false);
    setSelectedInspection(null);
  };

  const handleEditFromDetails = () => {
    if (selectedInspection) {
      setEditingInspection(selectedInspection);
      setShowDetails(false);
      setShowForm(true);
    }
  };

  const handleIQCTesting = (invoice: any) => {
    setSelectedPO(invoice);
    setShowIQCModal(true);
  };

  const handleIQCCompletion = async (poId: number, testResults: any) => {
    try {
      // Validate input parameters
      if (!poId || !testResults) {
        console.error('Invalid parameters for IQC completion');
        toast.error('Invalid test results. Please try again.');
        return;
      }

      if (typeof testResults.overallRating !== 'number' || isNaN(testResults.overallRating)) {
        console.error('Invalid overall rating:', testResults.overallRating);
        toast.error('Invalid rating value. Please try again.');
        return;
      }

      // Update PO status based on IQC testing results - CORRECTED WORKFLOW
      let newStatus = 'CREATED'; // Default status
      
      if (testResults.overallRating >= 5.0) {
        newStatus = 'FULLY_RECEIVED'; // Fully accepted
      } else if (testResults.overallRating >= 3.0 && testResults.overallRating < 5.0) {
        newStatus = 'PARTLY_RECEIVED'; // Partly accepted
      } else if (testResults.overallRating < 3.0) {
        newStatus = 'FULLY_REJECTED'; // Fully rejected
      }

      // Update PO with IQC results
      await actions.updatePurchaseOrder(poId, {
        status: newStatus,
        iqcCompleted: true,
        iqcCompletedBy: testResults.testedBy,
        iqcCompletedAt: testResults.testedAt,
        iqcRating: testResults.overallRating,
        iqcResults: testResults,
        updatedAt: new Date().toISOString()
      });

      // Update related invoices status based on IQC results
      const relatedInvoices = state.invoices.filter(inv => inv.poNumber === state.purchaseOrders.find(po => po.id === poId)?.poNumber);
      
      for (const invoice of relatedInvoices) {
        let invoiceStatus = 'Pending';
        
        if (testResults.overallRating >= 5.0) {
          invoiceStatus = 'Fully Accepted'; // Ready for floor manager dispatch
        } else if (testResults.overallRating >= 3.0 && testResults.overallRating < 5.0) {
          invoiceStatus = 'Partly Accepted'; // Ready for floor manager dispatch
        } else if (testResults.overallRating < 3.0) {
          invoiceStatus = 'Rejected'; // Not submitted to manager
        }

        console.log('Updating invoice:', invoice.id, 'with status:', invoiceStatus);
        const updateResult = await actions.updateInvoice(invoice.id, {
          status: invoiceStatus,
          receivedStatus: testResults.overallRating >= 3.0 ? 'Received' : 'Not Received',
          qualityTested: true,
          qualityTestedBy: testResults.testedBy,
          qualityTestedAt: testResults.testedAt,
          qualityRating: testResults.overallRating,
          qualityNotes: testResults.overallNotes,
          updatedAt: new Date().toISOString()
        });
        console.log('Invoice update result:', updateResult);
      }

      toast.success('IQC testing completed successfully');
      setShowIQCModal(false);
      
      // Refresh data to show updated statuses
      await actions.fetchInvoices();
      await actions.fetchPurchaseOrders();
      
      // Force component re-render by updating state
      setShowIQCModal(false);
      
      // Additional debug logging
      console.log('After quality testing completion, checking invoice statuses...');
      setTimeout(async () => {
        // Force another data refresh after a delay
        await actions.fetchInvoices();
        await actions.fetchPurchaseOrders();
        
        console.log('Delayed check - current invoices:', state.invoices.map(inv => ({ 
          id: inv.id, 
          invoiceNumber: inv.invoiceNumber,
          status: inv.status, 
          qualityTested: inv.qualityTested,
          qualityRating: inv.qualityRating,
          poNumber: inv.poNumber
        })));
        
        // Check specifically for Fully Accepted/Partly Accepted invoices
        const acceptedInvoices = state.invoices.filter(inv => 
          inv.status === 'Fully Accepted' || inv.status === 'Partly Accepted'
        );
        console.log('Invoices ready for Floor Manager Dispatch:', acceptedInvoices.length, acceptedInvoices);
      }, 1500);
    } catch (error) {
      console.error('Failed to complete IQC testing:', error);
      toast.error('Failed to complete IQC testing');
    }
  };

  const handleInvoiceQualityTesting = async (invoiceId: number, testResults: any) => {
    try {
      // Update invoice status based on quality rating - NEW WORKFLOW
      let newStatus = 'Pending'; // Default to Pending after quality testing
      let newReceivedStatus = 'Not Received';
      
      if (testResults.overallRating >= 3.0) {
        // Accepted (3.0 or higher) → Ready for manager submission
        newStatus = 'Pending'; // Will be submitted to manager
        newReceivedStatus = 'Received';
      } else if (testResults.overallRating < 3.0) {
        // Rejected (< 3.0) → No submission to manager
        newStatus = 'Rejected';
        newReceivedStatus = 'Not Received';
        
        // Create alert for quality failure
        const invoice = state.invoices.find(inv => inv.id === invoiceId);
        if (invoice) {
          addAlert({
            type: 'system',
            message: `Quality test failed for invoice ${invoice.invoiceNumber} - Rating: ${testResults.overallRating}/5`,
            invoiceId: invoiceId,
            poId: invoice.poNumber || '',
            severity: 'high',
            section: 'quality-control',
            data: {
              invoiceNumber: invoice.invoiceNumber,
              qualityRating: testResults.overallRating,
              qualityNotes: testResults.overallNotes,
              testedBy: testResults.testedBy,
              testedAt: testResults.testedAt
            }
          });
        }
      }

      // Update invoice
      const updatedInvoices = state.invoices.map(invoice => 
        invoice.id === invoiceId 
          ? { 
              ...invoice, 
              status: newStatus,
              receivedStatus: newReceivedStatus,
              qualityTested: true,
              qualityTestedBy: testResults.testedBy,
              qualityTestedAt: testResults.testedAt,
              qualityRating: testResults.overallRating,
              qualityNotes: testResults.overallNotes,
              qualityImages: testResults.overallImages,
              updatedAt: new Date().toISOString()
            }
          : invoice
      );

      // Update context
      actions.updateInvoice(invoiceId, {
        status: newStatus,
        receivedStatus: newReceivedStatus,
        qualityTested: true,
        qualityTestedBy: testResults.testedBy,
        qualityTestedAt: testResults.testedAt,
        qualityRating: testResults.overallRating,
        qualityNotes: testResults.overallNotes,
        qualityImages: testResults.overallImages,
        updatedAt: new Date().toISOString()
      });

      // Send notifications for partly accepted or rejected invoices
      if (testResults.overallRating < 5) {
        // In a real app, you would send actual notifications
        console.log('Sending notifications to vendor and managers for quality issues');
        toast.info('Notifications sent to vendor and managers');
      }

      toast.success('Quality testing completed successfully');
    } catch (error) {
      console.error('Failed to complete quality testing:', error);
      throw error;
    }
  };

  const handleDownloadInvoice = (invoice: any) => {
    const vendor = state.vendors.find(v => v.id === invoice.vendorId);
    const invoiceContent = `
Invoice: ${invoice.invoiceNumber}
PO: ${invoice.poNumber}
Vendor: ${vendor?.name || invoice.vendorName}
Date: ${new Date(invoice.invoiceDate).toLocaleDateString()}
Total Amount: ${formatCurrency(invoice.totalAmount)}
Status: ${invoice.status}

Items:
${invoice.items.map((item: any, index: number) => {
  return `${index + 1}. ${item.skuName} (${item.skuCode}) - Qty: ${item.quantity} - Price: ${formatCurrency(item.unitPrice)} - Total: ${formatCurrency(item.totalPrice)}`;
}).join('\n')}
    `.trim();

    // Create and download file
    const blob = new Blob([invoiceContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoice-${invoice.invoiceNumber}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // CTA Button Handlers
  const handleDownloadPO = (po: any) => {
    const vendor = state.vendors.find(v => v.id === po.vendorId);
    const poContent = `
Purchase Order: ${po.poNumber}
Vendor: ${vendor?.name || 'Unknown Vendor'}
Date: ${new Date(po.createdAt).toLocaleDateString()}
Total Amount: ${formatCurrency(po.totalAmount)}
Status: ${po.status}

Items:
${po.items.map((item: any, index: number) => {
  const sku = state.skus.find(s => s.id === item.skuId);
  return `${index + 1}. ${sku?.name || `SKU ${item.skuId}`} - Qty: ${item.quantity} - Price: ${formatCurrency(item.unitPrice)}`;
}).join('\n')}
    `.trim();

    // Create and download file
    const blob = new Blob([poContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `po-${po.poNumber}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleWhatsAppSharePO = (po: any) => {
    const vendor = state.vendors.find(v => v.id === po.vendorId);
    const message = `PO ${po.poNumber} - ${vendor?.name || 'Unknown Vendor'} - ${formatCurrency(po.totalAmount)} - ${po.status}`;
    const whatsappLink = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappLink, '_blank');
  };

  const handleIQCComplete = async (poId: number, testResults: any) => {
    try {
      // Calculate overall status based on test results
      let overallStatus = '';
      const allItems = testResults.items;
      const fullyAccepted = allItems.every((item: any) => item.rejectedQuantity === 0);
      const fullyRejected = allItems.every((item: any) => item.acceptedQuantity === 0);
      
      if (fullyAccepted) {
        overallStatus = 'Fully Accepted';
      } else if (fullyRejected) {
        overallStatus = 'Fully Rejected';
      } else {
        overallStatus = 'Partly Accepted';
      }

      // Get the current PO to preserve all data
      const po = state.purchaseOrders.find(p => p.id === poId);
      if (!po) {
        toast.error('Purchase order not found');
        return;
      }

      // Update individual item IQC data while preserving original item data
      const updatedItems = po.items.map((originalItem: any) => {
        const testItem = testResults.items.find((item: any) => item.itemId === originalItem.id);
        if (testItem) {
          return {
            ...originalItem, // Preserve all original item data
            receivedQuantity: testItem.receivedQuantity,
            acceptedQuantity: testItem.acceptedQuantity,
            rejectedQuantity: testItem.rejectedQuantity,
            iqcTested: true,
            iqcTestedBy: 1,
            iqcTestedAt: new Date().toISOString(),
            iqcNotes: testItem.notes || ''
          };
        }
        return originalItem;
      });

      // Recalculate total amount based on original unit prices and quantities
      const recalculatedTotalAmount = updatedItems.reduce((sum, item) => 
        sum + (item.unitPrice * item.quantity), 0
      );

      // Update PO with all preserved data and IQC results
      const updatedPO = {
        ...po, // Preserve all original PO data
        items: updatedItems,
        totalAmount: recalculatedTotalAmount,
        status: overallStatus,
        iqcCompleted: true,
        iqcCompletedBy: 1,
        iqcCompletedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      await actions.updatePurchaseOrder(poId, updatedPO);

      toast.success(`IQC testing completed. Status: ${overallStatus}`);
      setShowIQCModal(false);
      setSelectedPO(null);
    } catch (error) {
      console.error('Failed to complete IQC testing:', error);
      toast.error('Failed to complete IQC testing');
    }
  };

  const currentInspection = selectedInspection 
    ? state.qualityInspections.find(i => i.id === selectedInspection)
    : null;

  const editingInspectionData = editingInspection 
    ? state.qualityInspections.find(i => i.id === editingInspection)
    : null;

  return (
    <AppLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Quality Management</h1>
            <p className="text-muted-foreground">Manage quality inspections and control processes</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            <Button size="sm" onClick={handleCreateInspection}>
              <Plus className="h-4 w-4 mr-2" />
              New Inspection
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-2 md:grid-cols-4">
            <TabsTrigger value="iqc-ready" className="flex items-center gap-1 sm:gap-2">
              <Search className="h-4 w-4" />
              <span className="hidden sm:inline">IQC Ready</span>
              <span className="sm:hidden">IQC</span>
            </TabsTrigger>
            <TabsTrigger value="dashboard" className="flex items-center gap-1 sm:gap-2">
              <BarChart3 className="h-4 w-4" />
              <span className="hidden sm:inline">Dashboard</span>
              <span className="sm:hidden">Dash</span>
            </TabsTrigger>
            <TabsTrigger value="reports" className="flex items-center gap-1 sm:gap-2">
              <FileText className="h-4 w-4" />
              <span className="hidden sm:inline">Reports</span>
              <span className="sm:hidden">Rpt</span>
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-1 sm:gap-2">
              <Filter className="h-4 w-4" />
              <span className="hidden sm:inline">Settings</span>
              <span className="sm:hidden">Set</span>
            </TabsTrigger>
          </TabsList>

          {/* IQC Ready Tab */}
          <TabsContent value="iqc-ready" className="space-y-4">
            <Card>
              <CardContent>
                {(() => {
                  // Show invoices that are either ready for testing or have been tested
                  const filteredInvoices = state.invoices.filter(invoice => {
                    // Show invoices with "Created" status (ready for testing) OR tested invoices with "Fully Accepted"/"Partly Accepted" status
                    return (invoice.status === 'Created' && !invoice.qualityTested) || 
                           (invoice.status === 'Fully Accepted' || invoice.status === 'Partly Accepted');
                  }).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()); // Sort newest first
                  
                  console.log('Total invoices:', state.invoices.length);
                  console.log('Filtered invoices for quality control:', filteredInvoices.length);
                  console.log('Filtered invoices:', filteredInvoices);
                  console.log('All invoices with qualityTested status:', state.invoices.map(inv => ({ id: inv.id, status: inv.status, qualityTested: inv.qualityTested })));
                  return filteredInvoices.length > 0;
                })() ? (
                  <div className="space-y-4">
                    {(() => {
                      // Use the same filtering logic as above - show invoices that are either ready for testing or have been tested
                      const filteredInvoices = state.invoices.filter(invoice => {
                        return (invoice.status === 'Created' && !invoice.qualityTested) || 
                               (invoice.status === 'Fully Accepted' || invoice.status === 'Partly Accepted');
                      }).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()); // Sort newest first
                      
                      return filteredInvoices.map((invoice, index) => {
                        const vendor = state.vendors.find(v => v.id === invoice.vendorId);
                        return (
                          <div key={`${invoice.id}-${invoice.status}-${invoice.qualityTested}`} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 bg-muted/50 rounded-lg border hover:shadow-sm transition-all gap-4">
                            <div className="flex-1 space-y-2">
                              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                                <h3 className="font-semibold text-foreground">{invoice.invoiceNumber}</h3>
                                <div className="flex gap-2">
                                  <Badge variant="outline" className="text-xs">
                                    {invoice.poNumber}
                                  </Badge>
                                  {/* Status tag based on quality testing results */}
                                  {(() => {
                                    console.log(`Invoice ${invoice.invoiceNumber} - Status: ${invoice.status}, QualityTested: ${invoice.qualityTested}, QualityRating: ${invoice.qualityRating}`);
                                    return null;
                                  })()}
                                  {invoice.status === 'Fully Accepted' && (
                                    <Badge variant="default" className="text-xs bg-green-100 text-green-800 border-green-200">
                                      Fully Accepted
                                    </Badge>
                                  )}
                                  {invoice.status === 'Partly Accepted' && (
                                    <Badge variant="secondary" className="text-xs bg-yellow-100 text-yellow-800 border-yellow-200">
                                      Partly Accepted
                                    </Badge>
                                  )}
                                  {invoice.status === 'Created' && !invoice.qualityTested && (
                                    <Badge variant="outline" className="text-xs bg-blue-100 text-blue-800 border-blue-200">
                                      Ready for QC
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              
                              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                <div>
                                  <p className="text-muted-foreground">Vendor</p>
                                  <p className="font-medium">{vendor?.name || invoice.vendorName}</p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground">Amount</p>
                                  <p className="font-medium">{formatCurrency(invoice.totalAmount)}</p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground">Items</p>
                                  <p className="font-medium">{invoice.items.length} items</p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground">Status</p>
                                  <p className="font-medium">{invoice.receivedStatus}</p>
                                </div>
                              </div>

                              <div className="mt-3">
                                <p className="text-sm font-medium text-muted-foreground mb-2">Items for Quality Testing:</p>
                                <div className="space-y-1">
                                  {invoice.items.map((item, index) => {
                                    const sku = state.skus.find(s => s.id === item.skuId);
                                    return (
                                      <div key={item.id || index} className="flex items-center justify-between text-sm p-3 rounded border bg-background/50">
                                        <div className="flex-1">
                                          <div className="flex items-center gap-2">
                                            <span className="font-medium">
                                              {sku ? sku.name : item.skuName}
                                            </span>
                                            <span className="text-muted-foreground">
                                              ({item.skuCode})
                                            </span>
                                          </div>
                                          <div className="text-xs text-muted-foreground">
                                            Qty: {item.quantity} | Unit Price: {formatCurrency(item.unitPrice)} | Total: {formatCurrency(item.totalPrice)}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-2 sm:ml-4">
                              <Button
                                variant="default"
                                size="sm"
                                disabled={invoice.status === 'Fully Accepted' || invoice.status === 'Partly Accepted'}
                                onClick={() => {
                                  console.log('Starting quality testing for invoice:', invoice);
                                  // Find the related PO by poNumber and open PO-based IQC modal with correct poId
                                  const relatedPO = state.purchaseOrders.find(po => po.poNumber === invoice.poNumber);
                                  if (!relatedPO) {
                                    console.warn('Related PO not found for invoice', invoice.invoiceNumber, 'poNumber:', invoice.poNumber);
                                    toast.error('Related PO not found for this invoice');
                                    return;
                                  }
                                  setSelectedPO(relatedPO);
                                  setSelectedInvoice(invoice);
                                  setShowIQCModal(true);
                                }}
                                className="flex items-center gap-2 w-full sm:w-auto"
                              >
                                <Search className="h-4 w-4" />
                                <span className="hidden sm:inline">
                                  {invoice.status === 'Fully Accepted' || invoice.status === 'Partly Accepted' 
                                    ? 'Quality Testing Complete' 
                                    : 'Start Quality Testing'
                                  }
                                </span>
                                <span className="sm:hidden">
                                  {invoice.status === 'Fully Accepted' || invoice.status === 'Partly Accepted' 
                                    ? 'QC Done' 
                                    : 'Start QC'
                                  }
                                </span>
                              </Button>
                              
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDownloadInvoice(invoice)}
                                className="flex items-center gap-2 w-full sm:w-auto"
                              >
                                <Download className="h-4 w-4" />
                                <span className="hidden sm:inline">Download</span>
                              </Button>
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <div className="flex flex-col items-center gap-4">
                      <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center">
                        <Search className="h-8 w-8" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold">No Quality Control Invoices</h3>
                        <p className="text-sm">
                          Invoices ready for testing or completed quality control will appear here
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-4">
            <QualityDashboard
              inspections={state.qualityInspections}
              isLoading={state.isLoading}
            />
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Quality Reports</CardTitle>
                <CardDescription>Generate and view quality control reports</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                        <FileText className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold">Inspection Summary</h4>
                        <p className="text-sm text-muted-foreground">Monthly quality report</p>
                      </div>
                    </div>
                  </Card>

                  <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                        <BarChart3 className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold">Vendor Performance</h4>
                        <p className="text-sm text-muted-foreground">Quality by supplier</p>
                      </div>
                    </div>
                  </Card>

                  <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900 rounded-lg flex items-center justify-center">
                        <Search className="h-5 w-5 text-orange-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold">Defect Analysis</h4>
                        <p className="text-sm text-muted-foreground">Defect trends and patterns</p>
                      </div>
                    </div>
                  </Card>

                  <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center">
                        <Download className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold">Export Data</h4>
                        <p className="text-sm text-muted-foreground">Download inspection data</p>
                      </div>
                    </div>
                  </Card>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Quality Control Settings</CardTitle>
                <CardDescription>Configure quality inspection parameters and standards</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h4 className="font-semibold mb-3">Inspection Standards</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">Minimum Pass Rate</p>
                        <p className="text-sm text-muted-foreground">Minimum acceptable quality score</p>
                      </div>
                      <div className="text-right">
                        <span className="text-2xl font-bold">90%</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">Critical Defect Threshold</p>
                        <p className="text-sm text-muted-foreground">Maximum critical defects allowed</p>
                      </div>
                      <div className="text-right">
                        <span className="text-2xl font-bold">0</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">Major Defect Threshold</p>
                        <p className="text-sm text-muted-foreground">Maximum major defects allowed</p>
                      </div>
                      <div className="text-right">
                        <span className="text-2xl font-bold">3</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold mb-3">Notification Settings</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">Failed Inspection Alerts</p>
                        <p className="text-sm text-muted-foreground">Notify when inspections fail</p>
                      </div>
                      <div className="w-12 h-6 bg-green-500 rounded-full relative">
                        <div className="w-5 h-5 bg-white rounded-full absolute right-0.5 top-0.5"></div>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">Critical Defect Alerts</p>
                        <p className="text-sm text-muted-foreground">Immediate notification for critical defects</p>
                      </div>
                      <div className="w-12 h-6 bg-green-500 rounded-full relative">
                        <div className="w-5 h-5 bg-white rounded-full absolute right-0.5 top-0.5"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Quality Inspection Form Modal */}
        {showForm && (
          <QualityInspectionForm
            inspection={editingInspectionData}
            onSave={handleSaveInspection}
            onCancel={handleCloseForm}
          />
        )}

        {/* Quality Inspection Details Modal */}
        {showDetails && currentInspection && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-background rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <QualityInspectionDetails
                  inspection={currentInspection}
                  onEdit={handleEditFromDetails}
                  onClose={handleCloseDetails}
                />
              </div>
            </div>
          </div>
        )}

        {/* IQC Testing Modal */}
        {showIQCModal && selectedPO && (
          <IQCTestingModal
            isOpen={showIQCModal}
            onClose={() => {
              console.log('Closing IQC testing modal');
              setShowIQCModal(false);
              setSelectedPO(null);
            }}
            po={selectedPO}
            onComplete={handleIQCCompletion}
            currentUser={state.users?.[0] || { id: 1, name: 'Default User', role: 'admin' }}
          />
        )}

        {/* Invoice Quality Testing Modal */}
        {showInvoiceQualityModal && selectedInvoice && (
          <InvoiceQualityTestingModal
            isOpen={showInvoiceQualityModal}
            onClose={() => {
              setShowInvoiceQualityModal(false);
              setSelectedInvoice(null);
            }}
            invoice={selectedInvoice}
            onComplete={handleInvoiceQualityTesting}
            currentUser={state.users?.[0] || { id: 1, name: 'Default User', role: 'admin' }}
          />
        )}
      </div>
    </AppLayout>
  );
};

export default QualityManagement;
